/**
 * Login error copy + the classifier that maps a NextAuth signIn result to exactly
 * ONE of three distinct messages. Kept as a pure function (no React) so it's unit-
 * testable directly and shared by the login form. Mirrors ikroot-admin's
 * src/lib/auth/login-messages.ts.
 *
 * Four cases, distinguished by the shape NextAuth returns:
 *  - BAD_CRED  : authorize() returned null → res.error="CredentialsSignin" (no special code)
 *  - LOCKOUT   : authorize() threw LockoutError → res.error="CredentialsSignin", code="locked"
 *  - REACTIVATE: authorize() threw ReactivateNoticeError → code="reactivate" (2.2 §13.5)
 *  - SYSTEM    : a system/DB error (or any non-CredentialsSignin type, e.g. "Configuration")
 *                → an HONEST message that does NOT imply the credentials were wrong.
 */
export const LOGIN_MESSAGE = {
  /** (a) genuinely wrong identifier/password — dual-identifier wording. */
  BAD_CRED: "Email, username, or password is incorrect.",
  /** (b) account temporarily locked (5 failed attempts / 15 min — §32.8). */
  LOCKOUT: "Too many failed attempts. Please wait 15 minutes and try again.",
  /**
   * (c) the account is soft-deleted and still within its grace window (2.2 §13.5).
   * Reached ONLY after the password verified, so this message is shown exclusively to
   * someone who proved they own the account — an attacker probing the address gets
   * BAD_CRED, byte-identical to a nonexistent account (§21.9 enumeration-safety).
   * Deliberately says nothing about deletion: the detail is out-of-band, in the email.
   */
  REACTIVATE: "Check your email — we've sent you a link to continue.",
  /** (d) a system/infrastructure error — says nothing about the credentials. */
  SYSTEM: "Something went wrong — please try again.",
} as const;

/** The code a LockoutError carries (see authorize-credentials.ts). */
export const LOCKED_CODE = "locked";

/** The code a ReactivateNoticeError carries (see authorize-credentials.ts). */
export const REACTIVATE_CODE = "reactivate";

export interface SignInResultLike {
  error?: string | null;
  code?: string | null;
}

/**
 * Map a signIn({ redirect:false }) result to the message to display. `null`/no error
 * → "" (success / nothing to show). A thrown signIn (network) is a system error and
 * should use LOGIN_MESSAGE.SYSTEM at the call site.
 */
export function loginErrorMessage(res: SignInResultLike | null | undefined): string {
  if (!res?.error) return "";
  // Only the "CredentialsSignin" type is a real credential/lockout outcome; every
  // other type Auth.js surfaces (e.g. "Configuration" for a thrown DB error) is a
  // system error and must NOT imply the credentials were wrong.
  if (res.error !== "CredentialsSignin") return LOGIN_MESSAGE.SYSTEM;
  if (res.code === LOCKED_CODE) return LOGIN_MESSAGE.LOCKOUT;
  if (res.code === REACTIVATE_CODE) return LOGIN_MESSAGE.REACTIVATE;
  return LOGIN_MESSAGE.BAD_CRED;
}
